/**
 * Adversary claim VO on the lieVo bus.
 * Much quieter than Scripture; ducked further while the Word is spoken.
 * Paths: public/audio/vo/lies/{lieId}-{level}.mp3
 */

import type { AudioBusGraph } from "./buses";
import type { AudioFrameInput } from "./frame";
import type { AudioLoader } from "./loader";
import type { AudioSettings } from "./settings";

function clipIdFor(lieId: string, level: number): string {
  return `lieVo.${lieId}.${level}`;
}

function pathFor(lieId: string, level: number): string {
  return `vo/lies/${lieId}-${level}`;
}

/** All stage clips for the Proclaim lie pack. */
export const PROCLAIM_LIE_VO_CLIPS: readonly { lieId: string; level: number }[] =
  [
    { lieId: "whisper", level: 3 },
    { lieId: "whisper", level: 2 },
    { lieId: "whisper", level: 1 },
    { lieId: "heavy-gloom", level: 3 },
    { lieId: "heavy-gloom", level: 2 },
    { lieId: "heavy-gloom", level: 1 },
    { lieId: "alone", level: 3 },
    { lieId: "alone", level: 2 },
    { lieId: "alone", level: 1 },
    { lieId: "wildfire", level: 3 },
    { lieId: "wildfire", level: 2 },
    { lieId: "wildfire", level: 1 },
    { lieId: "anchor-weight", level: 3 },
    { lieId: "anchor-weight", level: 2 },
    { lieId: "anchor-weight", level: 1 },
    { lieId: "nagging-question", level: 3 },
    { lieId: "nagging-question", level: 2 },
    { lieId: "nagging-question", level: 1 },
    { lieId: "empty-words", level: 3 },
    { lieId: "empty-words", level: 2 },
    { lieId: "empty-words", level: 1 },
  ];

export class LieVoicePlayer {
  private readonly context: AudioContext;
  private readonly graph: AudioBusGraph;
  private readonly loader: AudioLoader;

  private source: AudioBufferSourceNode | null = null;
  private gain: GainNode | null = null;
  private disposed = false;
  /** True while a clip is loading or playing — blocks concurrent play(). */
  private playing = false;
  /** Bumped on stop/dispose so in-flight loads don't start after cancel. */
  private playGen = 0;

  constructor(
    context: AudioContext,
    graph: AudioBusGraph,
    loader: AudioLoader,
  ) {
    this.context = context;
    this.graph = graph;
    this.loader = loader;
  }

  isPlaying(): boolean {
    return this.playing;
  }

  async preload(
    clips: readonly { lieId: string; level: number }[] = PROCLAIM_LIE_VO_CLIPS,
  ): Promise<void> {
    if (this.disposed) return;
    await Promise.all(
      clips.map(async ({ lieId, level }) => {
        const id = clipIdFor(lieId, level);
        if (this.loader.peek(id) !== undefined) return;
        await this.loader.load(id, pathFor(lieId, level));
      }),
    );
  }

  /**
   * Play a lie claim — **never interrupts**. Only one lie VO at a time;
   * if a claim is already speaking (or loading), this is a no-op.
   * @param lieId primary VO key (campaign voId or mechanical family id)
   * @param fallbackId optional second key if primary files are missing
   */
  async play(
    lieId: string,
    level: number,
    settings: Pick<AudioSettings, "lieVoEnabled" | "muted">,
    fallbackId?: string,
  ): Promise<boolean> {
    if (this.disposed) return false;
    if (!settings.lieVoEnabled || settings.muted) return false;
    // Claim the channel synchronously so concurrent play() calls don't both start
    if (this.playing) return false;
    this.playing = true;
    const gen = this.playGen;

    const tryLoad = async (id: string): Promise<AudioBuffer | null> => {
      const clipId = clipIdFor(id, level);
      let buffer = this.loader.peek(clipId) ?? null;
      if (buffer === undefined || buffer === null) {
        buffer = await this.loader.load(clipId, pathFor(id, level));
      }
      return buffer ?? null;
    };

    let buffer: AudioBuffer | null = null;
    try {
      buffer = await tryLoad(lieId);
      if (!buffer && fallbackId && fallbackId !== lieId) {
        buffer = await tryLoad(fallbackId);
      }
    } catch {
      if (this.playGen === gen) this.playing = false;
      return false;
    }

    // Cancelled by stop()/dispose or superseded while loading
    if (this.playGen !== gen || this.disposed) {
      if (this.playGen === gen) this.playing = false;
      return false;
    }

    if (!buffer) {
      this.playing = false;
      return false;
    }

    const source = this.context.createBufferSource();
    source.buffer = buffer;
    const gain = this.context.createGain();
    // Per-clip trim — bus already runs low vs Scripture
    gain.gain.value = 1;

    source.connect(gain);
    gain.connect(this.graph.getBus("lieVo"));

    this.source = source;
    this.gain = gain;

    source.onended = () => {
      if (this.source === source) {
        this.playing = false;
        this.source = null;
        this.gain = null;
      }
      try {
        source.disconnect();
        gain.disconnect();
      } catch {
        /* */
      }
    };

    try {
      source.start();
      return true;
    } catch {
      this.playing = false;
      this.source = null;
      this.gain = null;
      return false;
    }
  }

  /** Soft mute while sim paused. */
  tick(frame: AudioFrameInput): void {
    if (this.disposed || !this.gain) return;
    const target = frame.paused ? 0 : 1;
    const g = this.gain.gain;
    const now = this.context.currentTime;
    g.cancelScheduledValues(now);
    g.setTargetAtTime(target, now, 0.04);
  }

  stop(): void {
    this.playGen++;
    this.stopSourceOnly();
    this.playing = false;
  }

  private stopSourceOnly(): void {
    if (this.source) {
      try {
        this.source.onended = null;
        this.source.stop();
      } catch {
        /* */
      }
      try {
        this.source.disconnect();
      } catch {
        /* */
      }
      this.source = null;
    }
    if (this.gain) {
      try {
        this.gain.disconnect();
      } catch {
        /* */
      }
      this.gain = null;
    }
  }

  resetMission(): void {
    this.stop();
  }

  dispose(): void {
    this.disposed = true;
    this.stop();
  }
}
